import { use, useEffect } from "react";
import { AppState } from "react-native";
import { KycStatus } from "../__generated__/graphql";
import { AuthManagerContext } from "../contexts/AuthManager";

const KYC_STATUS_POLL_MS = 5000;

const AWAITING_DECISION_STATUSES: KycStatus[] = [KycStatus.Pending, KycStatus.Submitted];

/**
 * While the KYC decision is awaited (`Pending` / `Submitted`), periodically
 * refetches the current user. As soon as the decision is received server-side,
 * the new `kycStatus` makes the Navigator leave the IdVerificationPending screen
 * and polling stops.
 *
 * The `kycChanged` silent push already triggers that refresh on native, but it
 * never reaches the web app (nor a device where notifications are denied), and
 * the decision often lands after the user reached the pending screen.
 *
 * Polls are skipped while the app/tab is not active: AuthManager already
 * refreshes the user when it comes back to the foreground.
 */
export const useKycStatusPolling = (currentStatus?: KycStatus | null): void => {
    const { refreshUser } = use(AuthManagerContext);

    const shouldPoll = !!currentStatus && AWAITING_DECISION_STATUSES.includes(currentStatus);

    useEffect(() => {
        if (!shouldPoll) return;
        const interval = setInterval(() => {
            if (AppState.currentState === "active") {
                void refreshUser?.();
            }
        }, KYC_STATUS_POLL_MS);
        return () => clearInterval(interval);
    }, [shouldPoll, refreshUser]);
};
